/**
 * brandAnalytics.ts — per-microsite analytics configuration.
 *
 * Wave 1 policy (per product owner): code-only, version-controlled. No DB,
 * no admin UI. Edit values here, ship via PR, review in code.
 *
 * Each brand owns:
 *   - enabled        master kill-switch (false = no tags injected at all)
 *   - mode           direct | gtm | hybrid (overrides VITE_GOOGLE_TRACKING_MODE)
 *   - platforms      per-platform enable flags (ga4, googleAds, gtm, meta, clarity)
 *   - tagIds         GA4, Ads, GTM web/server, Meta Pixel, Clarity
 *   - conversionLabels   Google Ads conversion action labels per kind
 *   - conversionValues   default $ value per conversion kind (lead-value calc)
 *   - attribution    auto-stamped on every GA4/GTM event (source_brand etc.)
 *   - enhancedConversionsEnabled  per-brand opt-in for hashed user_data
 *   - debug          per-brand verbose logging
 *
 * Same GA4 + same Ads account across Wave 1 microsites. Use the `attribution.*`
 * fields and `site_domain` (auto-stamped) to filter inside GA4 / Looker / Ads.
 */
import type { BrandKey } from "./brandRegistry";

export type TrackingMode = "direct" | "gtm" | "hybrid";

export interface BrandAnalyticsConfig {
  enabled: boolean;
  mode: TrackingMode;
  platforms: {
    ga4: boolean;
    googleAds: boolean;
    gtm: boolean;
    meta: boolean;
    clarity: boolean;
  };
  tagIds: {
    ga4MeasurementId: string;
    googleAdsId: string;
    gtmWebContainerId: string;
    gtmServerContainerId: string;
    metaPixelId: string;
    clarityProjectId: string;
  };
  conversionLabels: {
    lead: string;
    quote: string;
    estimate: string;
    phone: string;
    email: string;
  };
  conversionValues: {
    defaultLead: number;
    quote: number;
    estimate: number;
    inspection: number;
    repair: number;
    replacement: number;
    emergency: number;
    commercial: number;
    phone: number;
    email: number;
  };
  attribution: {
    sourceBrand: string;
    micrositeTopic: string;
    parentBrand: string;
    license: string;
  };
  enhancedConversionsEnabled: boolean;
  debug: boolean;
}

// ---- Shared defaults (same GA4/Ads/Pixel/Clarity across Wave 1) -------------

const SHARED_TAG_IDS = {
  ga4MeasurementId: "G-KBZ32H7S4P",
  googleAdsId: "AW-17145271541",
  gtmWebContainerId: "",
  gtmServerContainerId: "",
  metaPixelId: "2106570799804251",
  clarityProjectId: "ufi2rfr7ve",
} as const;

const SHARED_LABELS = {
  lead: "rlRRCJmriOgaEPWpwO8_",
  quote: "rlRRCJmriOgaEPWpwO8_",
  estimate: "rlRRCJmriOgaEPWpwO8_",
  phone: "J7U-CJ-riOgaEPWpwO8_",
  email: "ZEWHCIWd5-gaEPWpwO8_",
} as const;

const DEFAULT_VALUES: BrandAnalyticsConfig["conversionValues"] = {
  defaultLead: 8000,
  quote: 12000,
  estimate: 8000,
  inspection: 500,
  repair: 3000,
  replacement: 15000,
  emergency: 5000,
  commercial: 25000,
  phone: 1,
  email: 1,
};

const PARENT_BRAND = "O'Brien Contracting";
const PARENT_LICENSE = "CCC1335947";

interface BrandAnalyticsOverrides {
  enabled?: boolean;
  mode?: TrackingMode;
  platforms?: Partial<BrandAnalyticsConfig["platforms"]>;
  tagIds?: Partial<BrandAnalyticsConfig["tagIds"]>;
  conversionLabels?: Partial<BrandAnalyticsConfig["conversionLabels"]>;
  conversionValues?: Partial<BrandAnalyticsConfig["conversionValues"]>;
  enhancedConversionsEnabled?: boolean;
  debug?: boolean;
}

const makeAnalytics = (
  sourceBrand: string,
  micrositeTopic: string,
  overrides: BrandAnalyticsOverrides = {},
): BrandAnalyticsConfig => ({
  enabled: overrides.enabled ?? true,
  mode: overrides.mode ?? "direct",
  platforms: {
    ga4: true,
    googleAds: true,
    gtm: false,
    meta: true,
    clarity: true,
    ...overrides.platforms,
  },
  tagIds: { ...SHARED_TAG_IDS, ...overrides.tagIds },
  conversionLabels: { ...SHARED_LABELS, ...overrides.conversionLabels },
  conversionValues: { ...DEFAULT_VALUES, ...overrides.conversionValues },
  attribution: {
    sourceBrand,
    micrositeTopic,
    parentBrand: PARENT_BRAND,
    license: PARENT_LICENSE,
  },
  enhancedConversionsEnabled: overrides.enhancedConversionsEnabled ?? false,
  debug: overrides.debug ?? false,
});

// ---- Per-brand registry ------------------------------------------------------

export const BRAND_ANALYTICS: Record<BrandKey, BrandAnalyticsConfig> = {
  obc: makeAnalytics(PARENT_BRAND, "main-roofing-company", {
    tagIds: {
      gtmWebContainerId: "GTM-NVSJL9V3",
      gtmServerContainerId: "GTM-WJ869BL3",
    },
    platforms: { gtm: false }, // flip to true to actually inject the web container
  }),
  rcf: makeAnalytics("RoofCertificateFlorida", "roof-certificate", {
    conversionValues: { defaultLead: 5000, quote: 5000, estimate: 5000, inspection: 500 },
  }),
  frr: makeAnalytics("Florida Roof Replacement", "roof-replacement", {
    conversionValues: { defaultLead: 15000, quote: 18000, estimate: 15000, replacement: 18000 },
  }),
  oru: makeAnalytics("O'Brien Roofing USA", "general-roofing"),
  orfl: makeAnalytics("O'Brien Roofing FL", "florida-roofing"),
  fcr: makeAnalytics("Florida Commercial Roofing", "commercial-roofing", {
    conversionValues: { defaultLead: 25000, quote: 30000, estimate: 25000, commercial: 35000 },
  }),
  ftr: makeAnalytics("Florida Tile Roofing", "tile-roofing", {
    conversionValues: { defaultLead: 18000, quote: 22000, estimate: 18000, replacement: 22000 },
  }),
  fsr: makeAnalytics("Florida Shingle Roofing", "shingle-roofing", {
    conversionValues: { defaultLead: 10000, quote: 12000, estimate: 10000, replacement: 12000 },
  }),
  fmr: makeAnalytics("Florida Metal Roofing", "metal-roofing", {
    conversionValues: { defaultLead: 20000, quote: 25000, estimate: 20000, replacement: 25000 },
  }),
  ck: makeAnalytics("Coating Kingz", "roof-coatings", {
    conversionValues: { defaultLead: 8000, quote: 10000, estimate: 8000, commercial: 15000 },
  }),
};

export function getBrandAnalytics(brand: BrandKey): BrandAnalyticsConfig {
  return BRAND_ANALYTICS[brand] ?? BRAND_ANALYTICS.obc;
}
